import { Option } from '@beeline/design-system-react/extracted-styles';

import { createSelector } from '@reduxjs/toolkit';
import { BookingObject, PlaceType, Room, TimePeriod } from 'src/types';
import { getDateTime } from 'src/utils/getDateTime';
import { splitTime } from 'src/utils/splitTime';

import { StoreState } from '../types';

import { defaultRoomBookingFilters } from './initialState';
import { RoomsState } from './types';

const emptyTimePeriod: TimePeriod = {
  from: undefined,
  to: undefined,
};

export const getRoomsState = (state: StoreState): RoomsState => state.rooms;

export const getRoomLocation = createSelector(getRoomsState, rooms => ({
  loading: rooms.locationLoading,
  error: rooms.locationError,
  town: rooms.town,
  building: rooms.building,
  floor: rooms.floor,
}));

export const getRooms = createSelector(
  getRoomsState,
  rooms => rooms.bookingObjects,
);

export const getRoomSlots = createSelector(getRoomsState, rooms => rooms.slots);

export const getRoomsTown = createSelector(getRoomsState, rooms => rooms.town);

export const getRoomsDate = createSelector(getRoomsState, rooms => rooms.date);

export const getRoomsFilters = createSelector(
  getRoomsState,
  rooms => rooms.filters,
);

export const getRoomsTime = createSelector(getRoomsState, rooms => rooms.time);

export const getRoomsTimeSplitted = createSelector(getRoomsTime, time =>
  time.split('—'),
);

export const getRoomsDateError = createSelector(
  getRoomsState,
  rooms => rooms.dateError,
);

export const getRoomsTimeError = createSelector(
  getRoomsState,
  rooms => rooms.timeError,
);

export const getRoomsTimePeriodUTC = createSelector(
  getRoomsDate,
  getRoomsTown,
  getRoomsTimeSplitted,
  getRoomsTimeError,
  (date, town, [timeFrom, timeTo], timeError) => {
    if (!date) return emptyTimePeriod;

    if (!date.trim()) return emptyTimePeriod;

    if (timeError) return emptyTimePeriod;

    if (!town) return emptyTimePeriod;

    const result: TimePeriod = {
      from: undefined,
      to: undefined,
    };

    const [fromHours, fromMinutes] = splitTime(timeFrom);
    const [toHours, toMinutes] = splitTime(timeTo);

    const fromDate = getDateTime({
      date: new Date(date),
      hours: fromHours,
      minutes: fromMinutes,
      seconds: 0,
      milliseconds: 0,
      timezoneOffset: town.timeZone,
      toUTC: true,
    });
    const toDate = getDateTime({
      date: new Date(date),
      hours: toHours,
      minutes: toMinutes,
      seconds: 0,
      milliseconds: 0,
      timezoneOffset: town.timeZone,
      toUTC: true,
    });
    if (toDate < fromDate) toDate.setDate(toDate.getDate() + 1);

    result.from = fromDate.toISOString();
    result.to = toDate.toISOString();

    return result;
  },
);

export const getRoomsTimePeriodLocal = createSelector(
  getRoomsDate,
  getRoomsTown,
  getRoomsTimeSplitted,
  getRoomsTimeError,
  (date, town, [timeFrom, timeTo], timeError) => {
    if (!date) return emptyTimePeriod;

    if (!date.trim()) return emptyTimePeriod;

    if (timeError) return emptyTimePeriod;

    if (!town) return emptyTimePeriod;

    const result: TimePeriod = {
      from: undefined,
      to: undefined,
    };

    const [fromHours, fromMinutes] = splitTime(timeFrom);
    const [toHours, toMinutes] = splitTime(timeTo);

    const fromDate = getDateTime({
      date: new Date(date),
      hours: fromHours,
      minutes: fromMinutes,
      seconds: 0,
      milliseconds: 0,
    });
    const toDate = getDateTime({
      date: new Date(date),
      hours: toHours,
      minutes: toMinutes,
      seconds: 0,
      milliseconds: 0,
    });

    result.from = fromDate.toISOString();
    result.to = toDate.toISOString();

    return result;
  },
);

export const getFilteredRooms = createSelector(
  getRooms,
  getRoomsFilters,
  (rooms, filters) => {
    if (!rooms.size) return rooms;

    if (filters === defaultRoomBookingFilters) return rooms;

    const filtered = new Map<string, BookingObject>();

    for (const [key, value] of rooms) {
      if (value.type !== PlaceType.Room) continue;

      const filteredBySeatCount =
        !filters.seatCount.length ||
        filters.seatCount.find(f => value.seatCount === f.value);
      const filteredByFlipChart = filters.withFlipChart
        ? value.withFlipChart
        : true;
      const filteredByTv = filters.withTv ? value.withTv : true;
      const filteredByVideoCalls = filters.withVideoCalls
        ? value.withVideoCalls
        : true;
      const filteredByWater = filters.withWater ? value.withWater : true;

      if (
        filteredBySeatCount &&
        filteredByFlipChart &&
        filteredByTv &&
        filteredByVideoCalls &&
        filteredByWater
      )
        filtered.set(key, value);
    }

    return filtered;
  },
);

export const getRoomsFiltersCount = createSelector(getRoomsFilters, filters => {
  return (
    +(filters.seatCount !== defaultRoomBookingFilters.seatCount) +
    +(filters.withFlipChart !== defaultRoomBookingFilters.withFlipChart) +
    +(filters.withTv !== defaultRoomBookingFilters.withTv) +
    +(filters.withVideoCalls !== defaultRoomBookingFilters.withVideoCalls) +
    +(filters.withWater !== defaultRoomBookingFilters.withWater)
  );
});

export const getCurrentRoom = createSelector(getRoomsState, rooms =>
  rooms.roomId
    ? ((rooms.bookingObjects.get(rooms.roomId) as Room) ?? undefined)
    : undefined,
);

export const getRoomsSeatCountOptions = createSelector(getRooms, rooms => {
  const distinct = new Set<number>();

  for (const value of rooms.values()) {
    if (value.type === PlaceType.Room && value.seatCount)
      distinct.add(value.seatCount);
  }

  const result: Option<number>[] = [];
  for (const value of distinct) {
    result.push({ id: value, value });
  }

  return result.sort((a, b) => a.value - b.value);
});
